import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto text-4xl text-foreground-300">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <h2 className="mt-4 text-xl font-heading font-semibold text-foreground-900">漫画未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">请返回首页浏览其他漫画</p>
            <Link to="/" className="mt-4 inline-block px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.categorySlug === comic.categorySlug || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 4);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: comic.title,
      description: comic.description,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
      datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate || comic.updateDate,
      dateModified: comic.updateDate,
      image: comic.coverImage,
      publisher: {
        '@type': 'Organization',
        name: '非麻瓜漫画',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `http://wa25w.cn/comic/${comic.slug}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://wa25w.cn/' },
        { '@type': 'ListItem', position: 2, name: comic.category, item: `http://wa25w.cn/category/${comic.categorySlug}` },
        { '@type': 'ListItem', position: 3, name: comic.title, item: `http://wa25w.cn/comic/${comic.slug}` },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title={`${comic.title} - 非麻瓜漫画免费韩漫在线阅读`}
        description={`${comic.description} 非麻瓜漫画提供${comic.title}免费下拉式在线阅读，作者${comic.author}，${comic.status}，评分${comic.rating}。`}
        keywords={`${comic.title},${comic.category}韩漫,韩国漫画,下拉式漫画,免费韩漫`}
        canonicalUrl={`http://wa25w.cn/comic/${comic.slug}`}
        ogType="article"
        ogImage={comic.coverImage}
        jsonLd={jsonLd}
      />

      <Navbar />

      <main className="pt-16">
        <section className="relative w-full min-h-[350px] md:min-h-[450px] flex items-center overflow-hidden">
          <img
            src={comic.coverImage}
            alt={`${comic.title} - 非麻瓜漫画`}
            title={`${comic.title} - 非麻瓜漫画免费韩漫在线阅读`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10">
            <Breadcrumb
              items={[
                { name: '首页', url: '/' },
                { name: comic.category, url: `/category/${comic.categorySlug}` },
                { name: comic.title, url: `/comic/${comic.slug}` },
              ]}
            />
            <div className="mt-6 flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-3/5 text-center lg:text-left">
                <div className="flex flex-wrap items-center gap-2 justify-center lg:justify-start mb-3">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-50/20 text-background-50 text-xs rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                  <span className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-500/80 text-background-50' : 'bg-foreground-500/60 text-background-50'
                  }`}>
                    {comic.status}
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight">
                  {comic.title}
                </h1>
                <p className="mt-3 text-sm text-background-200/80">作者：{comic.author}</p>
                <p className="mt-4 text-sm md:text-base text-background-200/80 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                  {comic.description}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3 justify-center lg:justify-start">
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="px-7 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-play-fill"></i>
                    </span>
                    开始阅读
                  </Link>
                  <button className="px-7 py-3 border-2 border-background-50/40 text-background-50 rounded-full text-sm font-medium hover:bg-background-50/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bookmark-line"></i>
                    </span>
                    加入书架
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-secondary-100">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1.5 bg-background-50 text-foreground-700 text-xs font-medium rounded-full mb-3">
                作品信息
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">作品详情</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-3xl mx-auto">
              <div className="text-center p-4 bg-background-50 rounded-lg">
                <p className="text-xs text-foreground-500 mb-1">状态</p>
                <p className="text-lg font-heading font-semibold text-primary-600">{comic.status}</p>
              </div>
              <div className="text-center p-4 bg-background-50 rounded-lg">
                <p className="text-xs text-foreground-500 mb-1">热度</p>
                <p className="text-lg font-heading font-semibold text-primary-600">{(comic.views / 10000).toFixed(0)}万</p>
              </div>
              <div className="text-center p-4 bg-background-50 rounded-lg">
                <p className="text-xs text-foreground-500 mb-1">评分</p>
                <p className="text-lg font-heading font-semibold text-accent-600">{comic.rating}</p>
              </div>
              <div className="text-center p-4 bg-background-50 rounded-lg">
                <p className="text-xs text-foreground-500 mb-1">总话数</p>
                <p className="text-lg font-heading font-semibold text-primary-600">{comic.chapters.length}话</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 max-w-3xl mx-auto mt-12">
              <div className="text-center p-4">
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{(comic.views / 10000).toFixed(0)}万</p>
                <p className="text-xs text-foreground-500 mt-1">总阅读</p>
              </div>
              <div className="text-center p-4">
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{(comic.favorites / 10000).toFixed(0)}万</p>
                <p className="text-xs text-foreground-500 mt-1">收藏数</p>
              </div>
              <div className="text-center p-4">
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{comic.reviews.length}万+</p>
                <p className="text-xs text-foreground-500 mt-1">评论数</p>
              </div>
              <div className="text-center p-4">
                <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">8.5万</p>
                <p className="text-xs text-foreground-500 mt-1">分享数</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-3xl">{comic.longDescription}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10">
              <Link
                to={`/comic/${comic.slug}`}
                className="bg-foreground-950 rounded-lg p-5 md:p-6 hover:bg-foreground-900 transition-colors cursor-pointer"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-700 mb-3">
                  <i className="ri-list-check text-xl text-background-50"></i>
                </span>
                <h3 className="text-lg font-heading font-semibold text-background-50">章节目录</h3>
                <p className="mt-1 text-xs text-foreground-400">共 {comic.chapters.length} 话，持续更新</p>
              </Link>

              {comic.chapters[0] && (
                <Link
                  to={`/comic/${comic.slug}`}
                  className="relative rounded-lg overflow-hidden p-5 md:p-6 group cursor-pointer"
                  style={{
                    backgroundImage: `url(${comic.coverImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <div className="absolute inset-0 bg-foreground-950/70 group-hover:bg-foreground-950/60 transition-colors"></div>
                  <div className="relative z-10">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-accent-500/80 text-background-50 text-xs rounded-full whitespace-nowrap mb-3">
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-flashlight-line text-xs"></i>
                      </span>
                      最新
                    </span>
                    <h3 className="text-lg font-heading font-semibold text-background-50">{comic.chapters[0].title}</h3>
                    <p className="mt-1 text-xs text-background-300">更新于 {comic.chapters[0].updateDate}</p>
                  </div>
                </Link>
              )}

              <div className="bg-background-50 rounded-lg p-5 md:p-6 cursor-pointer">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 mb-3">
                  <i className="ri-chat-1-line text-xl text-foreground-600"></i>
                </span>
                <h3 className="text-lg font-heading font-semibold text-foreground-900">讨论区</h3>
                <p className="mt-1 text-xs text-foreground-500">{comic.reviews.length} 条读者评论</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-4">章节目录</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.slice(0, 12).map((chapter) => (
                  <Link
                    key={chapter.id}
                    to={`/comic/${comic.slug}`}
                    className="flex items-center justify-between px-4 py-3 bg-background-50 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-700">{chapter.title}</span>
                    <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                  </Link>
                ))}
              </div>
              {comic.chapters.length > 12 && (
                <button className="mt-4 w-full py-3 bg-background-50 rounded-lg text-sm text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer">
                  查看全部 {comic.chapters.length} 话章节
                </button>
              )}
            </div>
          </div>
        </section>

        {comic.reviews.length > 0 && (
          <section className="py-10 md:py-14 bg-foreground-950">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1.5 bg-foreground-800 text-foreground-200 text-xs font-medium rounded-full mb-3">
                  读者评论
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50">热门评论</h2>
              </div>
              <div className="max-w-3xl mx-auto flex flex-col gap-4">
                {comic.reviews.map((review) => (
                  <div key={review.id} className="bg-foreground-900 rounded-lg p-5 md:p-6">
                    <div className="flex items-start gap-4">
                      <img
                        src={review.avatarUrl}
                        alt={review.userName}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                        loading="lazy"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-background-50">{review.userName}</span>
                          <span className="px-2 py-0.5 bg-foreground-800 text-foreground-300 text-xs rounded-full whitespace-nowrap">
                            {review.userLevel}
                          </span>
                          <span className="flex items-center ml-auto">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <span key={i} className="w-3 h-3 flex items-center justify-center text-accent-500">
                                <i className="ri-star-fill text-xs"></i>
                              </span>
                            ))}
                          </span>
                        </div>
                        <p className="text-sm text-foreground-300 leading-relaxed">{review.content}</p>
                        <p className="mt-2 text-xs text-foreground-500">{review.date}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center mt-6">
                <button className="px-6 py-2.5 bg-accent-500/80 text-background-50 rounded-full text-sm font-medium hover:bg-accent-500 transition-colors cursor-pointer whitespace-nowrap">
                  写评论
                </button>
              </div>
            </div>
          </section>
        )}

        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-50">
            <div className="w-full px-4 md:px-6 lg:px-10">
              <div className="mb-8">
                <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
                  猜你喜欢
                </span>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}